import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Insight, MetricCard, Grid } from '../components/UI';
import { supabase } from '../supabaseClient';
import { takeFocus } from '../utils/consoleBus';
import { getUserRole } from '../roles';
import { PASSAGE_LABELS, PASSAGE_PRICES, MONTHLY_PRICE, PLAY_CONSOLE_URL, planKey, priceOf } from '../constants/pricing';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const LVL = ['L0 exact','L1 broad','L2 city dropped','L3 no results'];
const LCL = ['#28a745','#fd7e14','#e06c2c','#dc3545'];

function useIsMobile() {
  const [m,setM] = React.useState(()=>typeof window!=='undefined'&&window.innerWidth<768);
  React.useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  return m;
}

function timeAgo(ts) {
  if (!ts) return 'Never';
  const ms = Date.now() - new Date(ts);
  if (ms < 3600000)  return Math.round(ms/60000)+'m ago';
  if (ms < 86400000) return Math.round(ms/3600000)+'h ago';
  return Math.round(ms/86400000)+'d ago';
}

export default function UserDetail() {
  const isMobile = useIsMobile();
  const [search,  setSearch]  = useState('');
  const [userId,  setUserId]  = useState('');
  const [profile, setProfile] = useState(null);
  const [authUser,setAuthUser]= useState(null);
  const [saves,   setSaves]   = useState([]);
  const [passes,  setPasses]  = useState([]);
  const [queries, setQueries] = useState([]);
  const [usage,   setUsage]   = useState([]);
  const [events,  setEvents]  = useState(null);   // user_events trail (app_open + screen_view)
  const [presence,setPresence]= useState(null);   // member_presence row
  const [searches, setSearches] = useState([]);   // search_invocations
  const [bookingsRows, setBookingsRows] = useState([]); // bookings
  const [loading, setLoading] = useState(false);
  const [msg,     setMsg]     = useState('');
  const [revoking,setRevoking]= useState(null);
  const [refundMsg,setRefundMsg]=useState('');
  const [myRole,  setMyRole]  = useState('admin');
  // ADM-USR-01 (May 15, 2026) — extended detail via admin_get_user_detail RPC.
  const [detail,    setDetail]    = useState(null);    // jsonb blob from RPC
  const [noteBody,  setNoteBody]  = useState('');
  const [noteSaving,setNoteSaving]= useState(false);
  const [extendDays,setExtendDays]= useState(7);
  const [extending, setExtending] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  // Abuse + Refund Status panel state (May 2026 migration)
  const [abuseInfo, setAbuseInfo] = useState(null);   // { flagged, locked_until, same_day_cancel_count, lifetime_refund_count, phone, phone_verified_at }
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [suspBusy, setSuspBusy] = useState(false);

  React.useEffect(()=>{
    supabase.auth.getUser().then(({data})=>{
      if(data?.user){ const em=data.user.email||''; setMyRole(getUserRole(em)); }
    });
  },[]);

  // Deep link from the Users console drawer ("Full profile & PDF") — auto-run
  // the lookup for the handed-off user id.
  React.useEffect(() => {
    const f = takeFocus(['userlookup']);
    if (f && f.value) { setSearch(f.value); lookup(f.value); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Branded activity report -> browser print dialog -> Save as PDF.
  // Pulls the dispute-evidence trio: access/terms records, purchases, and
  // post-purchase usage (app_open sessions + Sana activity + presence).
  const exportPdf = async () => {
    if (!userId) return;
    try {
      const [evRes, presRes, accRes, bkRes, srRes] = await Promise.all([
        supabase.from('user_events').select('event_type,event_data,occurred_at').eq('user_id', userId).order('occurred_at', { ascending: false }).limit(300),
        supabase.from('member_presence').select('last_seen,status').eq('user_id', userId).maybeSingle(),
        supabase.from('access_events').select('pass_label,price_usd,access_granted_at,terms_accepted_at,ip_address,user_agent,timezone,expires_at').eq('user_id', userId).order('created_at', { ascending: false }).limit(20),
        supabase.from('bookings').select('booking_type,status,total_amount,currency,sana_recommended,created_at').eq('user_id', userId).order('created_at', { ascending: false }).limit(40),
        supabase.from('search_invocations').select('query,city,created_at').eq('user_id', userId).order('created_at', { ascending: false }).limit(60),
      ]);
      const ev = evRes.data || [];
      const pres = presRes.data || null;
      const acc = accRes.data || [];
      const bks = bkRes.data || [];
      const srs = srRes.data || [];
      const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
      const fdt = ts => (ts ? new Date(ts).toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : '—');
      const kv = (k, v) => '<tr><td class="k">' + esc(k) + '</td><td>' + esc(v) + '</td></tr>';
      const idRows = [
        kv('Email', authUser?.email || '—'),
        kv('User ID', userId),
        kv('Account created', fdt(authUser?.created_at)),
        kv('Last sign-in (auth)', fdt(authUser?.last_sign_in_at)),
        kv('Provider', authUser?.provider || 'email'),
        kv('Phone', (abuseInfo?.phone || '—') + (abuseInfo?.phone_verified_at ? ' (verified ' + fdt(abuseInfo.phone_verified_at) + ')' : '')),
      ].join('');
      const presRows = pres
        ? kv('Last seen in app', fdt(pres.last_seen)) + kv('Status at last ping', pres.status || '—')
        : kv('Last seen in app', 'no presence recorded');
      const accRows = acc.length ? acc.map(a =>
        '<tr><td>' + fdt(a.access_granted_at) + '</td><td>' + esc(a.pass_label || '—') + '</td><td>$' + esc(a.price_usd ?? '—') + '</td><td>' + fdt(a.terms_accepted_at) + '</td><td>' + esc(a.ip_address || '—') + '</td><td>' + esc((a.user_agent || '—').slice(0, 60)) + '</td></tr>'
      ).join('') : '<tr><td colspan="6">No purchase access records.</td></tr>';
      const passRows = (passes || []).length ? passes.map(p =>
        '<tr><td>' + esc(p.plan_key || p.pass_type || '—') + '</td><td>' + esc(p.status || (p.cancelled_at ? 'cancelled' : 'active')) + '</td><td>' + fdt(p.created_at) + '</td><td>' + fdt(p.expires_at) + '</td></tr>'
      ).join('') : '<tr><td colspan="4">No passes.</td></tr>';
      const evLabel = e => e.event_type === 'app_open' ? 'Opened the app (session start)'
        : e.event_type === 'screen_view' ? 'Viewed ' + esc((e.event_data && e.event_data.screen) || 'screen')
        : esc(e.event_type);
      const evRows = ev.length ? ev.map(e => '<tr><td>' + fdt(e.occurred_at) + '</td><td>' + evLabel(e) + '</td></tr>').join('')
        : '<tr><td colspan="2">No app-session events recorded yet (activity tracking shipped June 2026).</td></tr>';
      const qRows = (queries || []).length ? queries.map(q =>
        '<tr><td>' + fdt(q.created_at) + '</td><td>' + esc((q.query || '').slice(0, 70)) + '</td><td>' + esc([q.intent_city, q.intent_country].filter(Boolean).join(', ') || '—') + '</td></tr>'
      ).join('') : '<tr><td colspan="3">No Sana queries.</td></tr>';
      const bkPdfRows = bks.length ? bks.map(b =>
        '<tr><td>' + fdt(b.created_at) + '</td><td>' + esc(b.booking_type || 'venue') + '</td><td>' + esc(b.status || '—') + '</td><td>' + esc(b.total_amount != null ? b.total_amount + ' ' + (b.currency || '') : '—') + '</td><td>' + (b.sana_recommended ? 'via Sana' : '—') + '</td></tr>'
      ).join('') : '<tr><td colspan="5">No bookings.</td></tr>';
      const srPdfRows = srs.length ? srs.map(s =>
        '<tr><td>' + fdt(s.created_at) + '</td><td>' + esc((s.query || '').slice(0, 60)) + '</td><td>' + esc(s.city || '—') + '</td></tr>'
      ).join('') : '<tr><td colspan="3">No searches.</td></tr>';
      const riskRows = [
        kv('Flagged', abuseInfo?.flagged ? 'YES — ' + (abuseInfo.flag_reason || '') : 'no'),
        kv('Lifetime refunds', abuseInfo?.lifetime_refund_count ?? 0),
        kv('Same-day cancels', abuseInfo?.same_day_cancel_count ?? 0),
        kv('Devices on account', abuseInfo?.device_count ?? 0),
        kv('Last chargeback', fdt(abuseInfo?.last_chargeback_at)),
      ].join('');
      const html = '<!doctype html><html><head><meta charset="utf-8"><title>Sanoire — user activity report</title><style>'
        + "body{font-family:Georgia,'Times New Roman',serif;color:#15242e;margin:40px;}"
        + '.eyebrow{font-family:system-ui,Arial,sans-serif;font-size:10px;letter-spacing:3px;color:#9F7E4F;text-transform:uppercase;}'
        + 'h1{font-style:italic;font-weight:400;font-size:24px;margin:4px 0 2px;}'
        + '.meta{font-family:system-ui;font-size:11px;color:#6a8494;margin-bottom:22px;}'
        + 'h2{font-size:14px;border-bottom:1px solid rgba(159,126,79,.45);padding-bottom:4px;margin:22px 0 8px;font-weight:500;}'
        + 'table{width:100%;border-collapse:collapse;font-family:system-ui;font-size:11.5px;}'
        + 'td,th{padding:5px 8px;border-bottom:1px solid #eee8dc;text-align:left;vertical-align:top;}'
        + 'td.k{color:#6a8494;width:190px;}'
        + 'th{color:#9F7E4F;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;font-weight:600;}'
        + '.note{font-size:10.5px;color:#6a8494;font-family:system-ui;margin-top:26px;border-top:1px solid #eee8dc;padding-top:8px;}'
        + '@page{margin:18mm;}'
        + '</style></head><body>'
        + '<div class="eyebrow">Sanoire · User activity report</div>'
        + '<h1>' + esc(authUser?.email || userId) + '</h1>'
        + '<div class="meta">Generated ' + fdt(new Date().toISOString()) + '</div>'
        + '<h2>Identity & account</h2><table>' + idRows + '</table>'
        + '<h2>Presence</h2><table>' + presRows + '</table>'
        + '<h2>Purchases & terms acceptance</h2><table><tr><th>Granted</th><th>Pass</th><th>Price</th><th>Terms accepted</th><th>IP</th><th>Device</th></tr>' + accRows + '</table>'
        + '<h2>Passes</h2><table><tr><th>Plan</th><th>Status</th><th>Created</th><th>Expires</th></tr>' + passRows + '</table>'
        + '<h2>App sessions & screens</h2><table><tr><th>When</th><th>Event</th></tr>' + evRows + '</table>'
        + '<h2>Sana activity (recent)</h2><table><tr><th>When</th><th>Query</th><th>Location</th></tr>' + qRows + '</table>'
        + '<h2>Bookings</h2><table><tr><th>When</th><th>Type</th><th>Status</th><th>Amount</th><th>Source</th></tr>' + bkPdfRows + '</table>'
        + '<h2>Search activity</h2><table><tr><th>When</th><th>Query</th><th>City</th></tr>' + srPdfRows + '</table>'
        + '<h2>Risk & refunds</h2><table>' + riskRows + '</table>'
        + '<div class="note">Generated from live Sanoire records: auth, access_events, user_passes, user_events, member_presence, sana_invocations. Post-purchase usage shown above is suitable as service-delivery evidence in payment disputes.</div>'
        + '</body></html>';
      const w = window.open('', '_blank', 'width=920,height=720');
      if (!w) { setActionMsg('Pop-up blocked — allow pop-ups for the report.'); return; }
      w.document.write(html); w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (_e) {} }, 450);
    } catch (e) { setActionMsg('Report failed: ' + (e.message || e)); }
  };

  // Suspend / reactivate (admin_set_suspension — SECURITY DEFINER, audit-logged).
  const toggleSuspension = async () => {
    if (!userId || !canFinancial || suspBusy) return;
    const suspending = !(abuseInfo && abuseInfo.suspended);
    let reason = null;
    if (suspending) {
      reason = window.prompt('Reason for suspension (logged to the audit trail):', '');
      if (reason === null) return;
    } else if (!window.confirm('Reactivate this account? The user regains full access immediately.')) {
      return;
    }
    setSuspBusy(true);
    const { error } = await supabase.rpc('admin_set_suspension', { p_user_id: userId, p_suspend: suspending, p_reason: reason || null });
    setSuspBusy(false);
    if (error) { setActionMsg('Suspension change failed: ' + error.message); return; }
    setActionMsg(suspending ? 'Account suspended.' : 'Account reactivated.');
    lookup(userId);
  };

  const lookup = async (idOverride) => {
    let id = (idOverride || search).trim();
    if (!id) return;
    // Email -> UUID resolution (the search box accepts either).
    if (id.includes('@')) {
      setLoading(true); setMsg('');
      const { data: rid, error: re } = await supabase.rpc('admin_user_id_by_email', { p_email: id });
      if (re || !rid) { setLoading(false); setMsg('No account found for ' + id + '.'); return; }
      id = rid;
    } else if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      setMsg('Enter a user UUID or an email address. (Member numbers like 7K3M-9P2X are display hashes and can\u2019t be searched \u2014 use the email.)');
      return;
    }
    setLoading(true); setMsg(''); setProfile(null); setAuthUser(null);
    setSaves([]); setPasses([]); setQueries([]); setUsage([]);
    setEvents(null); setPresence(null); setSearches([]); setBookingsRows([]);
    setUserId(id);

    setDetail(null); setActionMsg(''); setAbuseInfo(null);
    const [
      {data:hp}, {data:sl}, {data:sq}, {data:su}, {data:ps}, {data:au}, {data:dt},
      {data:abuse}, {data:susp}, {data:pref}, {data:uev}, {data:pres}, {data:srch}, {data:bkg}
    ] = await Promise.all([
      supabase.from('health_profiles').select('*').eq('user_id',id).maybeSingle(),
      supabase.from('saved_locations').select('location_id,created_at').eq('user_id',id).order('created_at',{ascending:false}).limit(20),
      supabase.from('sana_invocations').select('query,intent_type,intent_city,intent_country,fallback_level,latency_ms,created_at').eq('user_id',id).order('created_at',{ascending:false}).limit(20),
      supabase.from('sana_usage').select('date,turn_count').eq('user_id',id).order('date',{ascending:false}).limit(14),
      supabase.from('user_passes').select('*').eq('user_id',id).order('created_at',{ascending:false}),
      supabase.rpc('admin_get_user_by_id', { p_user_id: id }).maybeSingle(),
      // ADM-USR-01 — extended detail (bookings, inscriptions, diaries, tickets, notes)
      supabase.rpc('admin_get_user_detail', { p_user_id: id }),
      // May 2026 — abuse + refund status
      supabase.from('user_abuse_signals').select('flagged,flag_reason,flagged_at,same_day_cancel_count,lifetime_refund_count,device_count,last_chargeback_at').eq('user_id',id).maybeSingle(),
      supabase.from('user_suspensions').select('suspended,reason,suspended_at,reactivated_at,locked_until').eq('user_id',id).maybeSingle(),
      supabase.from('user_preferences').select('phone,phone_verified_at').eq('user_id',id).maybeSingle(),
      supabase.from('user_events').select('event_type,event_data,occurred_at').eq('user_id',id).order('occurred_at',{ascending:false}).limit(300),
      supabase.from('member_presence').select('last_seen,status').eq('user_id',id).maybeSingle(),
      supabase.from('search_invocations').select('query,city,created_at').eq('user_id',id).order('created_at',{ascending:false}).limit(50),
      supabase.from('bookings').select('booking_type,status,total_amount,currency,sana_recommended,created_at').eq('user_id',id).order('created_at',{ascending:false}).limit(25),
    ]);

    setProfile(hp); setSaves(sl||[]); setQueries(sq||[]); setUsage(su||[]); setPasses(ps||[]);
    setEvents(uev||[]); setPresence(pres||null);
    setSearches(srch||[]); setBookingsRows(bkg||[]);
    if (au) setAuthUser(au);
    if (dt && !dt.error) setDetail(dt);
    setAbuseInfo({
      flagged: !!(abuse?.flagged),
      flag_reason: abuse?.flag_reason || null,
      same_day_cancel_count: abuse?.same_day_cancel_count || 0,
      lifetime_refund_count: abuse?.lifetime_refund_count || 0,
      device_count: abuse?.device_count || 0,
      last_chargeback_at: abuse?.last_chargeback_at || null,
      suspended: !!(susp?.suspended),
      suspension_reason: susp?.reason || null,
      locked_until: susp?.locked_until || null,
      phone: pref?.phone || null,
      phone_verified_at: pref?.phone_verified_at || null,
    });
    if (!hp && !(sl?.length) && !(sq?.length) && !(ps?.length) && !dt) setMsg('No data found for this user ID.');
    setLoading(false);
  };

  const revokePass = async (passId) => {
    setRevoking(passId);
    await supabase.from('user_passes').update({ status:'revoked' }).eq('id', passId);
    setPasses(prev => prev.map(p => p.id === passId ? {...p, status:'revoked'} : p));
    setRevoking(null);
  };

  // Passage is billed by Google Play: the refund is issued in Play Console,
  // then recorded here by admin_record_play_refund, which enforces the policy
  // server-side. Eligibility: ANNUAL, within 14 days of purchase, not flagged,
  // not locked, and lifetime_refund_count must be 0.
  const annualPass = passes.find(p => planKey(p.pass_type) === 'ANNUAL' && !p.refunded_at && p.status !== 'refunded');
  const annualEligibility = (() => {
    if (!annualPass) return { ok: false, why: 'No active ANNUAL pass' };
    const daysSince = (Date.now() - new Date(annualPass.created_at)) / 86400000;
    if (daysSince > 14) return { ok: false, why: 'Outside 14-day refund window (' + Math.round(daysSince) + 'd old)' };
    if (abuseInfo?.flagged) return { ok: false, why: 'Account flagged (' + (abuseInfo.flag_reason || 'no reason given') + ')' };
    if (abuseInfo?.locked_until && new Date(abuseInfo.locked_until) > new Date()) return { ok: false, why: 'Account locked until ' + abuseInfo.locked_until.slice(0,10) };
    if ((abuseInfo?.lifetime_refund_count || 0) >= 1) return { ok: false, why: 'Lifetime refund limit reached (' + abuseInfo.lifetime_refund_count + ')' };
    return { ok: true, why: 'Eligible — ' + Math.round(14 - daysSince) + 'd remaining' };
  })();

  const processAnnualRefund = async () => {
    if (!annualPass || !annualEligibility.ok) return;
    window.open(PLAY_CONSOLE_URL, '_blank', 'noopener');
    if (!window.confirm('Refund the ANNUAL order in Google Play Console first. Has the $' + PASSAGE_PRICES.ANNUAL + ' refund been issued there? Recording it counts against the user\'s lifetime refund quota.')) return;
    setRefundProcessing(true); setRefundMsg('');
    try {
      const { error } = await supabase.rpc('admin_record_play_refund', { p_pass_id: annualPass.id, p_reason: 'requested_by_customer_14d_window' });
      if (error) throw new Error(error.message);
      setPasses(prev => prev.map(p => p.id === annualPass.id ? { ...p, status: 'refunded', refunded_at: new Date().toISOString() } : p));
      setRefundMsg('Refund recorded (issued in Google Play Console).');
      // Refresh the abuse panel so lifetime_refund_count updates
      lookup(userId);
    } catch (_e) {
      setRefundMsg('Refund failed: ' + _e.message);
    }
    setRefundProcessing(false);
  };

  const hasData = !!(profile || saves.length || queries.length || passes.length || authUser);

  // Group the raw event trail into sessions (new session after a 30-min gap).
  const agoTxt = ts => {
    if (!ts) return 'never';
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s / 60) + 'm ago';
    if (s < 86400) return Math.floor(s / 3600) + 'h ago';
    return Math.floor(s / 86400) + 'd ago';
  };
  const sessions = React.useMemo(() => {
    // Merge the navigation trail with the action streams (Sana, search,
    // bookings, saves) so each session shows where they went AND what they did.
    const items = [];
    (events || []).forEach(e => {
      const tms = +new Date(e.occurred_at);
      if (e.event_type === 'screen_view') items.push({ t: tms, kind: 'screen', text: (e.event_data && e.event_data.screen) || 'screen' });
      else items.push({ t: tms, kind: 'open' });
    });
    (queries || []).forEach(q => q.created_at && items.push({ t: +new Date(q.created_at), kind: 'action', text: 'Asked Sana: \u201C' + String(q.query || '').slice(0, 60) + '\u201D' }));
    (searches || []).forEach(s => s.created_at && items.push({ t: +new Date(s.created_at), kind: 'action', text: 'Searched: \u201C' + String(s.query || '').slice(0, 50) + '\u201D' + (s.city ? ' \u00B7 ' + s.city : '') }));
    (bookingsRows || []).forEach(b => b.created_at && items.push({ t: +new Date(b.created_at), kind: 'action', text: 'Booking \u2014 ' + (b.booking_type || 'venue') + (b.status ? ' (' + b.status + ')' : '') + (b.sana_recommended ? ' \u00B7 via Sana' : '') }));
    (saves || []).forEach(sv => sv.created_at && items.push({ t: +new Date(sv.created_at), kind: 'action', text: 'Saved a location' }));
    if (!items.length) return [];
    items.sort((a, b) => a.t - b.t);
    const out = [];
    let cur = null;
    items.forEach(it => {
      if (!cur || it.t - cur.last > 30 * 60000) { cur = { start: it.t, last: it.t, screens: [], actions: [] }; out.push(cur); }
      cur.last = it.t;
      if (it.kind === 'screen') { if (cur.screens[cur.screens.length - 1] !== it.text) cur.screens.push(it.text); }
      else if (it.kind === 'action') cur.actions.push(it.text);
    });
    return out.reverse();
  }, [events, queries, searches, bookingsRows, saves]);
  const topScreens = React.useMemo(() => {
    const m = {};
    (events || []).forEach(e => { if (e.event_type === 'screen_view') { const sn = (e.event_data && e.event_data.screen) || 'screen'; m[sn] = (m[sn] || 0) + 1; } });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [events]);
  const isLiveNow = presence && presence.last_seen && (Date.now() - new Date(presence.last_seen)) < 5 * 60000;
  const totalSpend = passes.reduce((s,p)=>s+priceOf(p.pass_type),0);
  const activePasses = passes.filter(p=>p.status==='active'&&new Date(p.expires_at)>new Date()).length;

  // ── LTV / subscription analysis (May 2026) ──────────────────────────────────
  // Separates MONTHLY value from ANNUAL value. A user can have both — e.g.
  // Monthly, then switched to Annual. We surface that history per-user.
  const monthlyPasses = passes.filter(p => planKey(p.pass_type) === 'MONTHLY');
  const oneTimePasses = passes.filter(p => planKey(p.pass_type) === 'ANNUAL');
  const hasMonthly = monthlyPasses.length > 0;
  // Sort MONTHLY rows by created_at ascending so cohorts read top-to-bottom in time order
  const monthlyPassesAsc = [...monthlyPasses].sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
  // Months active: sum of (min(expires_at, now) - created_at) / 30d, capped at >= 0
  const now = new Date();
  let monthsActiveTotal = 0;
  let monthlyPaidTotal = 0;
  monthlyPasses.forEach(p => {
    if (!p.created_at) return;
    const start = new Date(p.created_at);
    const endRaw = p.expires_at ? new Date(p.expires_at) : now;
    const end = endRaw < now ? endRaw : now;
    const months = Math.max(0, (end - start) / (30 * 86400000));
    monthsActiveTotal += months;
    monthlyPaidTotal  += months * MONTHLY_PRICE; // list price
  });
  const monthsActiveDisplay = monthsActiveTotal.toFixed(1);
  // Currently subscribed = at least one MONTHLY row with expires_at in future + status active
  const currentMonthlyActive = monthlyPasses.some(p =>
    p.status === 'active' && new Date(p.expires_at) > now);
  // Distinct subscription periods = consecutive runs of MONTHLY rows (cancelled+resub counts separately).
  // Simple proxy: count rows. This over-counts continuous monthly renewals if each renewal
  // creates a row, so we also show "subscription periods" only if rows > 1.
  const monthlyRowCount = monthlyPasses.length;
  // Churn risk: currently active MONTHLY user with no Sana query in last 30 days
  const lastSanaTs = queries[0]?.created_at ? new Date(queries[0].created_at) : null;
  const daysSinceLastSana = lastSanaTs ? Math.round((now - lastSanaTs) / 86400000) : null;
  const churnRiskElevated = currentMonthlyActive && (daysSinceLastSana == null || daysSinceLastSana > 30);

  const canFinancial = myRole === 'c_suite' || myRole === 'admin';

  // ADM-USR-01 — extend the user's active pass by N days (no Stripe charge).
  const extendPass = async () => {
    if (!userId || !extendDays) return;
    setExtending(true); setActionMsg('');
    try {
      const { data, error } = await supabase.rpc('admin_extend_user_pass', {
        p_user_id: userId,
        p_days: Number(extendDays),
        p_reason: 'admin_goodwill',
      });
      if (error) throw error;
      if (data && data.error) { setActionMsg('Extend failed: ' + data.error); }
      else { setActionMsg('Pass extended by ' + extendDays + ' days. New expiry: ' + (data?.new_expires_at || '').slice(0,10)); lookup(userId); }
    } catch (_e) { setActionMsg('Extend failed: ' + _e.message); }
    setExtending(false);
  };

  // ADM-USR-01 — add an internal admin note about this user.
  const addNote = async () => {
    if (!userId || !noteBody.trim()) return;
    setNoteSaving(true); setActionMsg('');
    try {
      const { data, error } = await supabase.rpc('admin_add_user_note', {
        p_user_id: userId,
        p_body: noteBody.trim(),
        p_tag: null,
      });
      if (error) throw error;
      setNoteBody(''); setActionMsg('Note saved.'); lookup(userId);
    } catch (_e) { setActionMsg('Note failed: ' + _e.message); }
    setNoteSaving(false);
  };

  // Helper — money formatting
  const fmtCents = (c) => c == null ? '—' : '$' + (Number(c)/100).toFixed(2);
  const fmtDate = (s) => s ? s.slice(0,10) : '—';

  return (
    <div style={{fontFamily:F}}>
      <div style={{marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:500,margin:0,color:'var(--color-text-primary)'}}>User lookup</h2>
        <p style={{fontSize:'.78rem',color:'var(--color-text-secondary)',margin:'4px 0 0'}}>Search by user UUID or email. View full history — passes, saves, Sana queries, health profile.</p>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:16}}>
        <input
          value={search}
          onChange={e=>setSearch(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&lookup()}
          placeholder="Paste user UUID or email..."
          style={{flex:1,padding:'8px 12px',borderRadius:8,border:'1px solid var(--color-border-secondary)',background:'var(--color-background-secondary)',color:'var(--color-text-primary)',fontSize:'.78rem',fontFamily:'monospace',outline:'none'}}
        />
        <button
          onClick={()=>lookup()}
          disabled={loading||!search.trim()}
          style={{padding:'8px 20px',borderRadius:8,background:'#314F5C',color:'#AFE1F6',border:'none',fontSize:'.78rem',fontWeight:500,cursor:'pointer',fontFamily:F,whiteSpace:'nowrap'}}
        >
          {loading ? 'Loading...' : 'Look up'}
        </button>
        {hasData ? (
          <button
            onClick={exportPdf}
            title="Branded activity report — print dialog, save as PDF"
            style={{padding:'8px 16px',borderRadius:8,background:'transparent',color:'#9F7E4F',border:'1px solid rgba(159,126,79,0.45)',fontSize:'.78rem',fontWeight:600,cursor:'pointer',fontFamily:F,whiteSpace:'nowrap'}}
          >
            Activity report (PDF)
          </button>
        ) : null}
      </div>

      {msg && <Insight type="info" style={{marginBottom:12}}>{msg}</Insight>}
      {refundMsg && <Insight type="warn" style={{marginBottom:12}}>{refundMsg}</Insight>}

      {hasData && (
        <>
          {/* Summary row */}
          <Grid cols={4}>
            <MetricCard label="Total spend"    value={'$'+totalSpend}      sub="all passes" accent="#9F7E4F" />
            <MetricCard label="Active passes"  value={activePasses}        sub="currently live" accent="#28a745" />
            <MetricCard label="Locations saved" value={saves.length}       sub="bookmarks" accent="#314F5C" />
            <MetricCard label="Sana queries"   value={queries.length}      sub="last 20 shown" accent="#6F8FA7" />
          </Grid>

          {/* Abuse + Refund Status panel (May 2026) */}
          {abuseInfo && (
            <Card>
              <CardTitle>Abuse + refund status</CardTitle>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:10}}>
                {[
                  { k:'Flagged', v: abuseInfo.flagged ? 'YES — ' + (abuseInfo.flag_reason||'no reason') : 'No', highlight: abuseInfo.flagged },
                  { k:'Locked until', v: abuseInfo.locked_until ? abuseInfo.locked_until.slice(0,16).replace('T',' ') : 'Not locked', highlight: !!abuseInfo.locked_until && new Date(abuseInfo.locked_until) > new Date() },
                  { k:'Suspended', v: abuseInfo.suspended ? 'YES — ' + (abuseInfo.suspension_reason||'') : 'No', highlight: abuseInfo.suspended },
                  { k:'Same-day cancels', v: String(abuseInfo.same_day_cancel_count), highlight: abuseInfo.same_day_cancel_count >= 1 },
                  { k:'Lifetime refunds', v: String(abuseInfo.lifetime_refund_count), highlight: abuseInfo.lifetime_refund_count >= 1 },
                  { k:'Devices known', v: String(abuseInfo.device_count), highlight: abuseInfo.device_count >= 3 },
                  { k:'Last chargeback', v: abuseInfo.last_chargeback_at ? abuseInfo.last_chargeback_at.slice(0,10) : 'None', highlight: !!abuseInfo.last_chargeback_at },
                  { k:'Phone', v: abuseInfo.phone || 'Not provided', highlight: false },
                  { k:'Phone verified', v: abuseInfo.phone_verified_at ? abuseInfo.phone_verified_at.slice(0,10) : 'Not verified', highlight: !abuseInfo.phone_verified_at },
                ].map(({k,v,highlight,link})=>(
                  <div key={k} style={{padding:'8px 10px',background: highlight ? '#fff5f0' : 'var(--color-background-secondary)',borderRadius:8,border: highlight ? '1px solid #dc354540' : 'none'}}>
                    <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>{k}</div>
                    <div style={{fontSize:'.75rem',color: highlight ? '#dc3545' : 'var(--color-text-primary)',fontWeight:500,wordBreak:'break-word'}}>
                      {link ? (
                        <a onClick={(e)=>{e.preventDefault(); setSearch(link); lookup(link);}} href="#" style={{color:'#6F8FA7',textDecoration:'none'}}>{v} →</a>
                      ) : v}
                    </div>
                  </div>
                ))}
              </div>
              {/* Suspend / reactivate — completes the abuse-protection stack */}
              <div style={{marginTop:14,padding:'10px 12px',background:'var(--color-background-secondary)',borderRadius:8}}>
                <div style={{fontSize:'.7rem',color:'var(--color-text-secondary)',marginBottom:6,fontWeight:500,letterSpacing:'.04em',textTransform:'uppercase'}}>Account access</div>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                  <button
                    onClick={toggleSuspension}
                    disabled={!canFinancial || suspBusy}
                    title={canFinancial ? (abuseInfo.suspended ? 'Restore full access' : 'Block the account (sign-in stays, paid features and purchases stop)') : 'Requires admin role'}
                    style={{padding:'7px 16px',borderRadius:6,background: !canFinancial ? '#9aaab3' : abuseInfo.suspended ? '#17a86a' : '#A32D2D',color:'#fff',border:'none',fontSize:'.72rem',fontWeight:600,cursor: canFinancial ? 'pointer' : 'not-allowed',fontFamily:F}}>
                    {suspBusy ? '…' : abuseInfo.suspended ? 'Reactivate account' : 'Suspend account'}
                  </button>
                  <span style={{fontSize:'.65rem',color:'var(--color-text-secondary)'}}>
                    {abuseInfo.suspended ? 'Suspended' + (abuseInfo.suspension_reason ? ' — ' + abuseInfo.suspension_reason : '') : 'Account in good standing. Suspension is logged to the audit trail.'}
                  </span>
                </div>
              </div>

              {/* Process Refund button — only for ANNUAL holders */}
              <div style={{marginTop:14,padding:'10px 12px',background:'var(--color-background-secondary)',borderRadius:8}}>
                <div style={{fontSize:'.7rem',color:'var(--color-text-secondary)',marginBottom:6,fontWeight:500,letterSpacing:'.04em',textTransform:'uppercase'}}>Process refund (ANNUAL, 14-day window)</div>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                  <button
                    onClick={processAnnualRefund}
                    disabled={!canFinancial || !annualEligibility.ok || refundProcessing}
                    title={annualEligibility.ok ? 'Refund in Google Play Console, then record it here' : annualEligibility.why}
                    style={{padding:'7px 16px',borderRadius:6,background: (canFinancial && annualEligibility.ok) ? '#9F7E4F' : '#9aaab3',color:'#fff',border:'none',fontSize:'.72rem',fontWeight:500,cursor: (canFinancial && annualEligibility.ok) ? 'pointer' : 'not-allowed',fontFamily:F}}>
                    {refundProcessing ? '…' : 'Record Play refund ($' + PASSAGE_PRICES.ANNUAL + ')'}
                  </button>
                  <span style={{fontSize:'.65rem',color: annualEligibility.ok ? '#28a745' : '#9F7E4F'}}>{annualEligibility.why}</span>
                </div>
              </div>
            </Card>
          )}

          {/* Auth user info if available */}
          {authUser && (<>
            <Card>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
                <CardTitle style={{marginBottom:0}}>Activity & sessions</CardTitle>
                <div style={{fontSize:'.76rem',fontWeight:600,color:isLiveNow?'#17a86a':'var(--color-text-secondary)'}}>
                  {isLiveNow ? '● Live now' : presence && presence.last_seen ? 'Last seen ' + agoTxt(presence.last_seen) : 'No presence recorded'}
                </div>
              </div>
              {topScreens.length > 0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:6,margin:'12px 0 2px'}}>
                  {topScreens.map(([sn, n]) => (
                    <span key={sn} style={{fontSize:'.7rem',fontWeight:600,padding:'3px 10px',borderRadius:99,background:'rgba(159,126,79,0.12)',color:'#9F7E4F'}}>{sn} · {n}</span>
                  ))}
                </div>
              )}
              {events === null ? null : sessions.length === 0 ? (
                <div style={{fontSize:'.78rem',color:'var(--color-text-secondary)',marginTop:10}}>
                  No session events recorded yet — the screen-activity trail starts with the June 2026 app build.
                </div>
              ) : (
                <div style={{marginTop:12}}>
                  {sessions.slice(0, 10).map((s, i) => {
                    const mins = Math.max(1, Math.round((s.last - s.start) / 60000));
                    const path = s.screens.slice(0, 8).join(' → ') + (s.screens.length > 8 ? ' · +' + (s.screens.length - 8) + ' more' : '');
                    return (
                      <div key={i} style={{display:'flex',gap:12,padding:'8px 0',borderBottom:'1px solid rgba(159,126,79,0.14)',fontSize:'.78rem',alignItems:'baseline'}}>
                        <span style={{color:'var(--color-text-secondary)',whiteSpace:'nowrap',minWidth:148,fontFamily:'monospace',fontSize:'.7rem'}}>
                          {new Date(s.start).toISOString().replace('T',' ').slice(0,16)} · {mins}m
                        </span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{color:'var(--color-text-primary)',lineHeight:1.55}}>{path || (s.actions.length ? 'Activity (pre-trail app build)' : 'Opened the app')}</div>
                          {s.actions.slice(0,6).map((a,j)=>(<div key={j} style={{color:'#9F7E4F',fontSize:'.74rem',lineHeight:1.65}}>&middot; {a}</div>))}
                          {s.actions.length>6 && <div style={{color:'var(--color-text-secondary)',fontSize:'.7rem'}}>+{s.actions.length-6} more actions</div>}
                        </div>
                      </div>
                    );
                  })}
                  {sessions.length > 10 && <div style={{fontSize:'.7rem',color:'var(--color-text-secondary)',marginTop:8}}>{sessions.length - 10} older sessions — full list in the PDF report.</div>}
                </div>
              )}
            </Card>

            <Card>
              <CardTitle>Account</CardTitle>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10}}>
                {[
                  ['Email', authUser.email],
                  ['Last sign in', timeAgo(authUser.last_sign_in_at)],
                  ['Created', authUser.created_at?.slice(0,10)],
                  ['Provider', authUser.provider || 'email'],
                ].map(([k,v])=>v?(
                  <div key={k} style={{padding:'8px 10px',background:'var(--color-background-secondary)',borderRadius:8}}>
                    <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>{k}</div>
                    <div style={{fontSize:'.78rem',color:'var(--color-text-primary)',fontWeight:500}}>{v}</div>
                  </div>
                ):null)}
              </div>
            </Card>
          </>)}

          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:16}}>
            {/* Passes */}
            <Card>
              <CardTitle>Passes ({passes.length})</CardTitle>
              {passes.length === 0 ? (
                <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No passes purchased.</div>
              ) : passes.map(p => {
                const expired = new Date(p.expires_at) < new Date();
                const status = p.status === 'refunded' ? 'refunded' : p.status === 'revoked' ? 'revoked' : expired ? 'expired' : 'active';
                const color = status==='active'?'#28a745':status==='refunded'?'#9F7E4F':'#dc3545';
                return (
                  <div key={p.id} style={{padding:'8px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:3}}>
                      <span style={{fontSize:'.78rem',fontWeight:500,color:'var(--color-text-primary)'}}>
                        {PASSAGE_LABELS[planKey(p.pass_type)]||p.pass_type} · ${priceOf(p.pass_type)}
                      </span>
                      <span style={{display:'inline-flex',gap:5,alignItems:'center'}}>
                        {p.purchase_source && <span style={{fontSize:'.62rem',padding:'1px 7px',borderRadius:8,background:'rgba(159,126,79,0.14)',color:'#9F7E4F',fontWeight:600}}>{p.purchase_source}{p.purchase_touchpoint ? ' · ' + p.purchase_touchpoint : ''}</span>}
                        <span style={{fontSize:'.62rem',padding:'1px 7px',borderRadius:8,background:color+'18',color,fontWeight:500}}>{status}</span>
                      </span>
                    </div>
                    <div style={{fontSize:'.65rem',color:'var(--color-text-secondary)',marginBottom:4}}>
                      {p.created_at?.slice(0,10)} → {p.expires_at?.slice(0,10)}
                      <a href={PLAY_CONSOLE_URL} target="_blank" rel="noopener noreferrer"
                        style={{marginLeft:8,color:'#6F8FA7',textDecoration:'none'}}>Orders in Play Console →</a>
                    </div>
                    {status === 'active' && (
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={()=>{ if(canFinancial) revokePass(p.id); }} disabled={!canFinancial || revoking===p.id}
                          style={{fontSize:'.62rem',color:'#dc3545',background:'none',border:'1px solid #dc354530',borderRadius:5,padding:'2px 8px',cursor:'pointer',fontFamily:F}}>
                          {revoking===p.id?'...':'Revoke'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>

            {/* Health profile */}
            <Card>
              <CardTitle>Health profile</CardTitle>
              {!profile ? (
                <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No health profile set.</div>
              ) : Object.entries({
                conditions: profile.conditions,
                medications_allergies: profile.medications_allergies,
                insurance_type: profile.insurance_type,
                wellness_interests: profile.wellness_interests,
                preferred_language: profile.preferred_language,
                blood_type: profile.blood_type,
                nationality: profile.nationality,
              }).filter(([,v])=>v).map(([k,v])=>(
                <div key={k} style={{padding:'5px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                  <div style={{fontSize:'.62rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.04em'}}>{k.replace(/_/g,' ')}</div>
                  <div style={{fontSize:'.75rem',color:'var(--color-text-primary)',marginTop:1}}>{v}</div>
                </div>
              ))}
            </Card>

            {/* Saved locations */}
            <Card>
              <CardTitle>Saved locations ({saves.length})</CardTitle>
              {saves.length === 0 ? (
                <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No saves.</div>
              ) : saves.map((s,i)=>(
                <div key={i} style={{padding:'4px 0',borderBottom:'.5px solid var(--color-border-tertiary)',fontSize:'.72rem',display:'flex',justifyContent:'space-between'}}>
                  <span style={{color:'var(--color-text-secondary)',fontFamily:'monospace',fontSize:'.65rem'}}>ID {s.location_id}</span>
                  <span style={{color:'var(--color-text-secondary)'}}>{s.created_at?.slice(0,10)}</span>
                </div>
              ))}
            </Card>

            {/* Sana queries */}
            <Card>
              <CardTitle>Recent Sana queries ({queries.length})</CardTitle>
              {queries.length === 0 ? (
                <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No queries.</div>
              ) : queries.map((q,i)=>(
                <div key={i} style={{padding:'5px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                  <div style={{fontSize:'.75rem',color:'var(--color-text-primary)',fontWeight:500}}>{q.query}</div>
                  <div style={{display:'flex',gap:8,marginTop:2,flexWrap:'wrap'}}>
                    <span style={{fontSize:'.62rem',color:LCL[q.fallback_level||0]}}>{LVL[q.fallback_level||0]}</span>
                    {q.intent_city&&<span style={{fontSize:'.62rem',color:'var(--color-text-secondary)'}}>{q.intent_city}{q.intent_country?', '+q.intent_country:''}</span>}
                    <span style={{fontSize:'.62rem',color:'var(--color-text-secondary)',marginLeft:'auto'}}>{q.latency_ms}ms · {timeAgo(q.created_at)}</span>
                  </div>
                </div>
              ))}
            </Card>

            {/* Sana usage */}
            {usage.length > 0 && (
              <Card>
                <CardTitle>Sana usage (last 14d)</CardTitle>
                {usage.map(u=>(
                  <div key={u.date} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'.5px solid var(--color-border-tertiary)',fontSize:'.75rem'}}>
                    <span style={{color:'var(--color-text-secondary)'}}>{u.date}</span>
                    <span style={{fontWeight:500,color:u.turn_count>=40?'#dc3545':u.turn_count>=20?'#fd7e14':'#28a745'}}>{u.turn_count} queries</span>
                  </div>
                ))}
              </Card>
            )}
          </div>

          {/* ── LTV / Subscription analysis (May 2026) ─────────────────────────── */}
          {(hasMonthly || oneTimePasses.length > 1) && (
            <Card>
              <CardTitle>
                {hasMonthly ? 'Subscription LTV' : 'Purchase history summary'}
              </CardTitle>
              {hasMonthly ? (
                <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10,marginBottom:14}}>
                    <div style={{padding:'8px 10px',background:'var(--color-background-secondary)',borderRadius:8}}>
                      <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>Months active (MONTHLY)</div>
                      <div style={{fontSize:'.95rem',color:'var(--color-text-primary)',fontWeight:500}}>{monthsActiveDisplay}</div>
                    </div>
                    <div style={{padding:'8px 10px',background:'var(--color-background-secondary)',borderRadius:8}}>
                      <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>Paid via MONTHLY</div>
                      <div style={{fontSize:'.95rem',color:'var(--color-text-primary)',fontWeight:500}}>${monthlyPaidTotal.toFixed(2)}</div>
                    </div>
                    <div style={{padding:'8px 10px',background:'var(--color-background-secondary)',borderRadius:8}}>
                      <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>Subscription periods</div>
                      <div style={{fontSize:'.95rem',color:'var(--color-text-primary)',fontWeight:500}}>{monthlyRowCount}</div>
                    </div>
                    <div style={{padding:'8px 10px',background:churnRiskElevated?'#fff5f0':'var(--color-background-secondary)',borderRadius:8,border:churnRiskElevated?'1px solid #dc354530':'none'}}>
                      <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>Churn risk</div>
                      <div style={{fontSize:'.78rem',color:churnRiskElevated?'#dc3545':'#28a745',fontWeight:500}}>
                        {!currentMonthlyActive ? 'Not active' : churnRiskElevated ? 'Elevated' : 'Healthy'}
                      </div>
                      {currentMonthlyActive && (
                        <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',marginTop:2}}>
                          {daysSinceLastSana == null ? 'No Sana queries logged' : daysSinceLastSana + 'd since last Sana query'}
                        </div>
                      )}
                    </div>
                  </div>
                  {monthlyRowCount > 1 && (
                    <>
                      <div style={{fontSize:'.62rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:6}}>Cohort history (oldest first)</div>
                      {monthlyPassesAsc.map((p, idx) => {
                        const start = p.created_at?.slice(0,10);
                        const end   = p.expires_at?.slice(0,10);
                        const cancelled = p.status === 'cancelled' || p.status === 'revoked';
                        const expired = new Date(p.expires_at) < now;
                        const label = cancelled ? 'cancelled' : expired ? 'lapsed' : 'active';
                        const color = label === 'active' ? '#28a745' : label === 'cancelled' ? '#dc3545' : '#9F7E4F';
                        return (
                          <div key={p.id || idx} style={{padding:'5px 0',borderBottom:'.5px solid var(--color-border-tertiary)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                            <span style={{fontSize:'.72rem',color:'var(--color-text-primary)'}}>Period {idx+1} · {start} → {end}</span>
                            <span style={{fontSize:'.6rem',padding:'1px 7px',borderRadius:8,background:color+'18',color,fontWeight:500}}>{label}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              ) : (
                <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>
                  One-time pass purchases only. Lifetime spend: <span style={{color:'var(--color-text-primary)',fontWeight:500}}>${totalSpend}</span> across {oneTimePasses.length} pass{oneTimePasses.length === 1 ? '' : 'es'}.
                </div>
              )}
            </Card>
          )}

          {/* ── ADM-USR-01 Extended sections ───────────────────────────────────── */}
          {detail && (
            <>
              {/* Quick actions: extend pass + add internal note */}
              <Card>
                <CardTitle>Quick actions</CardTitle>
                {actionMsg && <Insight type={actionMsg.startsWith('Pass extended') || actionMsg === 'Note saved.' ? 'ok' : 'warn'} style={{marginBottom:10}}>{actionMsg}</Insight>}
                <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:14}}>
                  {/* Extend pass */}
                  <div style={{padding:'10px 12px',background:'var(--color-background-secondary)',borderRadius:8}}>
                    <div style={{fontSize:'.7rem',color:'var(--color-text-secondary)',marginBottom:6,fontWeight:500,letterSpacing:'.04em',textTransform:'uppercase'}}>Extend active pass</div>
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                      <input type="number" min="1" max="365" value={extendDays} onChange={e=>setExtendDays(e.target.value)}
                        style={{width:70,padding:'7px 9px',borderRadius:6,border:'1px solid var(--color-border-secondary)',background:'#fff',fontSize:'.75rem',fontFamily:F,outline:'none'}} />
                      <span style={{fontSize:'.7rem',color:'var(--color-text-secondary)'}}>days</span>
                      <button onClick={extendPass} disabled={!canFinancial || extending || !detail.active_pass}
                        style={{padding:'7px 14px',marginLeft:'auto',borderRadius:6,background: (canFinancial && detail.active_pass) ? '#314F5C' : '#9aaab3',color:'#AFE1F6',border:'none',fontSize:'.7rem',fontWeight:500,cursor:'pointer',fontFamily:F}}>
                        {extending ? '…' : 'Extend'}
                      </button>
                    </div>
                    {!detail.active_pass && <div style={{fontSize:'.6rem',color:'#9F7E4F',marginTop:4}}>No active pass to extend.</div>}
                  </div>
                  {/* Add internal note */}
                  <div style={{padding:'10px 12px',background:'var(--color-background-secondary)',borderRadius:8}}>
                    <div style={{fontSize:'.7rem',color:'var(--color-text-secondary)',marginBottom:6,fontWeight:500,letterSpacing:'.04em',textTransform:'uppercase'}}>Add internal note</div>
                    <textarea value={noteBody} onChange={e=>setNoteBody(e.target.value)} placeholder="Internal note about this user (visible only to admin team)..."
                      style={{width:'100%',minHeight:42,padding:'7px 9px',borderRadius:6,border:'1px solid var(--color-border-secondary)',background:'#fff',fontSize:'.72rem',fontFamily:F,outline:'none',resize:'vertical',boxSizing:'border-box'}} />
                    <button onClick={addNote} disabled={noteSaving || !noteBody.trim()}
                      style={{marginTop:6,padding:'5px 14px',borderRadius:6,background:'#314F5C',color:'#AFE1F6',border:'none',fontSize:'.7rem',fontWeight:500,cursor:'pointer',fontFamily:F,float:'right'}}>
                      {noteSaving ? '…' : 'Save note'}
                    </button>
                    <div style={{clear:'both'}}/>
                  </div>
                </div>
              </Card>

              {/* Rollup stats */}
              {detail.stats && (
                <Grid cols={4}>
                  <MetricCard label="Bookings" value={detail.stats.booking_count || 0} sub={(detail.stats.booking_count_confirmed||0)+' confirmed'} accent="#28a745" />
                  <MetricCard label="Inscriptions" value={detail.stats.inscription_count || 0} sub="verified reviews" accent="#9F7E4F" />
                  <MetricCard label="Support tickets" value={(detail.support_tickets||[]).length} sub={(detail.stats.open_ticket_count||0)+' open'} accent="#dc3545" />
                  <MetricCard label="Passes (lifetime)" value={detail.stats.pass_count_lifetime || 0} sub={(detail.stats.pass_count_refunded||0)+' refunded'} accent="#6F8FA7" />
                </Grid>
              )}

              <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:16}}>
                {/* Bookings */}
                <Card>
                  <CardTitle>Bookings ({(detail.bookings||[]).length})</CardTitle>
                  {(detail.bookings||[]).length === 0 ? (
                    <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No bookings yet.</div>
                  ) : detail.bookings.map(b => (
                    <div key={b.id} style={{padding:'6px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
                        <span style={{fontSize:'.75rem',fontWeight:500,color:'var(--color-text-primary)'}}>{b.venue_name || b.booking_ref}</span>
                        <span style={{fontSize:'.6rem',padding:'1px 7px',borderRadius:8,background: b.status==='confirmed'?'#28a74518':b.status==='cancelled'?'#dc354518':'#9F7E4F18',color: b.status==='confirmed'?'#28a745':b.status==='cancelled'?'#dc3545':'#9F7E4F',fontWeight:500}}>{b.status}</span>
                      </div>
                      <div style={{fontSize:'.62rem',color:'var(--color-text-secondary)'}}>
                        {b.booking_type} · {b.venue_city}{b.venue_country?', '+b.venue_country:''} · {fmtDate(b.created_at)}
                      </div>
                    </div>
                  ))}
                </Card>

                {/* Inscriptions */}
                <Card>
                  <CardTitle>Inscriptions ({(detail.inscriptions||[]).length})</CardTitle>
                  {(detail.inscriptions||[]).length === 0 ? (
                    <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No reviews written.</div>
                  ) : detail.inscriptions.map(i => (
                    <div key={i.id} style={{padding:'6px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                      <div style={{fontSize:'.72rem',color:'var(--color-text-primary)',lineHeight:1.4}}>{i.body}</div>
                      <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',marginTop:3,display:'flex',gap:6,flexWrap:'wrap'}}>
                        <span>{i.city}{i.country?', '+i.country:''}</span>
                        {i.would_return && <span>· {i.would_return}</span>}
                        {i.verification && <span>· {i.verification}</span>}
                        <span style={{marginLeft:'auto'}}>{fmtDate(i.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </Card>

                {/* Trip diaries */}
                <Card>
                  <CardTitle>Trip diaries ({(detail.trip_diaries||[]).length})</CardTitle>
                  {(detail.trip_diaries||[]).length === 0 ? (
                    <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No trip diaries yet.</div>
                  ) : detail.trip_diaries.map(d => (
                    <div key={d.id} style={{padding:'5px 0',borderBottom:'.5px solid var(--color-border-tertiary)',fontSize:'.72rem'}}>
                      <span style={{color:'var(--color-text-primary)',fontWeight:500}}>{d.city}{d.country?', '+d.country:''}</span>
                      <span style={{color:'var(--color-text-secondary)',marginLeft:6}}>· {d.days_count} days · {fmtDate(d.trip_start_date)}</span>
                      {d.co_signed && <span style={{marginLeft:6,fontSize:'.6rem',color:'#9F7E4F'}}>co-signed</span>}
                    </div>
                  ))}
                </Card>

                {/* Support tickets */}
                <Card>
                  <CardTitle>Support tickets ({(detail.support_tickets||[]).length})</CardTitle>
                  {(detail.support_tickets||[]).length === 0 ? (
                    <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No support tickets.</div>
                  ) : detail.support_tickets.map(t => (
                    <div key={t.id} style={{padding:'6px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
                        <span style={{fontSize:'.72rem',color:'var(--color-text-primary)',fontWeight:500}}>{t.subject}</span>
                        <span style={{fontSize:'.6rem',padding:'1px 7px',borderRadius:8,background: t.status==='resolved'?'#28a74518':t.status==='in_progress'?'#fd7e1418':'#dc354518',color: t.status==='resolved'?'#28a745':t.status==='in_progress'?'#fd7e14':'#dc3545',fontWeight:500}}>{t.status}</span>
                      </div>
                      <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)'}}>
                        {t.priority || 'P3'} · {t.category || 'other'}{t.refunded?' · refunded '+fmtCents(t.refund_amount_cents):''} · {fmtDate(t.created_at)}
                      </div>
                    </div>
                  ))}
                </Card>
              </div>

              {/* Internal admin notes — always show */}
              <Card>
                <CardTitle>Internal admin notes ({(detail.notes||[]).length})</CardTitle>
                {(detail.notes||[]).length === 0 ? (
                  <div style={{fontSize:'.75rem',color:'var(--color-text-secondary)'}}>No notes yet. Add one above.</div>
                ) : detail.notes.map(n => (
                  <div key={n.id} style={{padding:'8px 0',borderBottom:'.5px solid var(--color-border-tertiary)'}}>
                    <div style={{fontSize:'.72rem',color:'var(--color-text-primary)',lineHeight:1.45,whiteSpace:'pre-wrap'}}>{n.body}</div>
                    <div style={{fontSize:'.6rem',color:'var(--color-text-secondary)',marginTop:3}}>
                      {n.author} · {fmtDate(n.created_at)} {n.tag ? <span style={{marginLeft:6,padding:'0 6px',background:'#9F7E4F15',color:'#9F7E4F',borderRadius:6}}>{n.tag}</span> : null}
                    </div>
                  </div>
                ))}
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
